import { useCallback, useEffect, useState } from "react";
import { Routes, Route, Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./lib/auth-context";
import { api } from "@repo/api-client";
import { LoginPage } from "./pages/LoginPage";
import { HomePage } from "./pages/HomePage";
import { ContentPage } from "./pages/ContentPage";
import { ProductsPage } from "./pages/ProductsPage";
import { CartPage } from "./pages/CartPage";
import { CheckoutPage } from "./pages/CheckoutPage";
import { PracticePage } from "./pages/PracticePage";
import { MoodPage } from "./pages/MoodPage";
import { PassPage } from "./pages/PassPage";
import { ProfilePage } from "./pages/ProfilePage";
import { GamificationPage } from "./pages/GamificationPage";
import { LegacyInfoPage } from "./pages/LegacyInfoPage";
import { AdminPage } from "./pages/AdminPage";
import { AboutPage } from "./pages/AboutPage";
import { DonatePage } from "./pages/DonatePage";
import { VolunteerPage } from "./pages/VolunteerPage";
import { EventsListPage } from "./pages/EventsListPage";
import { EventDetailPage } from "./pages/EventDetailPage";
import { OpsShellPage } from "./pages/OpsShellPage";
import { CheckInPage } from "./pages/CheckInPage";
import { PosPage } from "./pages/PosPage";
import { DonationInfoPage } from "./pages/DonationInfoPage";
import { ContactPage } from "./pages/ContactPage";
import { HealingPage } from "./pages/HealingPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DreamsPage } from "./pages/DreamsPage";
import { BadgesPage } from "./pages/BadgesPage";
import { LeaderboardPage } from "./pages/LeaderboardPage";
import { BlogPage, BlogPostPage } from "./pages/BlogPage";
import { MagazinePage } from "./pages/MagazinePage";
import { PodcastPage } from "./pages/PodcastPage";
import { PortfolioPage } from "./pages/PortfolioPage";
import { RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "./pages/AuthPages";
import { useTheme } from "./lib/theme-context";
import CoursesPage from "./pages/CoursesPage";
import CourseDetailPage from "./pages/CourseDetailPage";
import TeachersPage from "./pages/TeachersPage";
import TeacherProfilePage from "./pages/TeacherProfilePage";
import LibraryPage from "./pages/LibraryPage";
import WellnessPage from "./pages/WellnessPage";
import StoriesPage from "./pages/StoriesPage";
import StoryDetailPage from "./pages/StoryDetailPage";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <section className="container" style={{ padding: "5rem 0" }}>
        <div className="card" style={{ maxWidth: "720px", margin: "0 auto", textAlign: "center" }}>
          <h1 style={{ marginBottom: "0.6rem" }}>403 - Forbidden</h1>
          <p style={{ marginBottom: "1.2rem", color: "var(--c-ink-75)" }}>
            Admin access is required to open this page.
          </p>
          <Link to="/" className="btn btn--brand">Back to home</Link>
        </div>
      </section>
    );
  }
  return <>{children}</>;
}

function SenaRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="page-loading">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "sena" && user.role !== "admin") {
    return (
      <section className="container" style={{ padding: "5rem 0" }}>
        <div className="card" style={{ maxWidth: "720px", margin: "0 auto", textAlign: "center" }}>
          <h1 style={{ marginBottom: "0.6rem" }}>403 - Forbidden</h1>
          <p style={{ marginBottom: "1.2rem", color: "var(--c-ink-75)" }}>
            Sena (staff) access is required to open this page.
          </p>
          <Link to="/ops" className="btn btn--brand">Go to Ops Shell</Link>
        </div>
      </section>
    );
  }
  return <>{children}</>;
}

export function App() {
  const { user, profile, logout } = useAuth();
  const { settings } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [cartItemCount, setCartItemCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [secondaryNavExpanded, setSecondaryNavExpanded] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    const q = searchQuery.trim();
    setSearchOpen(false);
    setSearchQuery("");
    if (q) navigate(`/wisdom?q=${encodeURIComponent(q)}`);
  }

  // Close search on Escape key
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && searchOpen) { setSearchOpen(false); setSearchQuery(""); }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [searchOpen]);

  const refreshCartBadge = useCallback(async () => {
    if (!user) {
      setCartItemCount(0);
      return;
    }

    try {
      const cart = await api.getCart();
      const count = (cart.items ?? []).reduce((sum, item) => sum + item.quantity, 0);
      setCartItemCount(count);
    } catch {
      setCartItemCount(0);
    }
  }, [user]);

  useEffect(() => {
    void refreshCartBadge();
  }, [refreshCartBadge, location.pathname]);

  useEffect(() => {
    const syncCartBadge = () => {
      void refreshCartBadge();
    };

    window.addEventListener("cart:updated", syncCartBadge);
    return () => window.removeEventListener("cart:updated", syncCartBadge);
  }, [refreshCartBadge]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  const profileInitial = (profile?.display_name || user?.email || "?")
    .trim()
    .slice(0, 1)
    .toUpperCase();

  const publicNavItems = [
    { to: "/", label: "Home", icon: "🏠" },
    { to: "/wisdom", label: "Wisdom", icon: "📚" },
    { to: "/courses", label: "Courses", icon: "🎓" },
    { to: "/events", label: "Events", icon: "🗓️" },
    { to: "/wellness", label: "Wellness", icon: "🌿" },
    { to: "/stories", label: "Stories", icon: "💬" },
    { to: "/library", label: "Library", icon: "📖" },
    { to: "/teachers", label: "Teachers", icon: "🙏" },
    { to: "/about", label: "About", icon: "🌿" },
    { to: "/donate", label: "Donate", icon: "💛" },
    { to: "/shop", label: "Shop", icon: "🛍️" },
  ];

  const authNavItems = [
    { to: "/", label: "Home", icon: "🏠" },
    { to: "/wisdom", label: "Wisdom", icon: "📚" },
    { to: "/courses", label: "Courses", icon: "🎓" },
    { to: "/practice", label: "Practice", icon: "🧘" },
    { to: "/events", label: "Events", icon: "🗓️" },
    { to: "/wellness", label: "Wellness", icon: "🌿" },
    { to: "/stories", label: "Stories", icon: "💬" },
    { to: "/library", label: "Library", icon: "📖" },
    { to: "/shop", label: "Shop", icon: "🛍️" },
  ];

  const secondaryNavItems = user
    ? [
        { to: "/practice", label: "Today", icon: "🧘" },
        { to: "/dashboard", label: "Progress", icon: "📈" },
        ...(settings.enableGamification ? [{ to: "/gamification", label: "Badges", icon: "🏅" }] : []),
        { to: "/events", label: "Community", icon: "🗓️" },
        { to: "/shop", label: "Shop", icon: "🛍️" },
        { to: "/profile", label: "Account", icon: "👤" },
      ]
    : [
        { to: "/wisdom", label: "Wisdom", icon: "📖" },
        { to: "/events", label: "Events", icon: "🎧" },
        { to: "/about", label: "About", icon: "🌿" },
        { to: "/donate", label: "Support", icon: "💛" },
        { to: "/shop", label: "Shop", icon: "🛍️" },
        { to: "/login", label: "Account", icon: "✨" },
      ];

  const mobileDockItems = user
    ? [
        { to: "/practice", label: "Today", icon: "🧘" },
        { to: "/dashboard", label: "Progress", icon: "📈" },
        { to: "/events", label: "Events", icon: "🗓️" },
        { to: "/shop", label: "Shop", icon: "🛍️" },
        { to: "/profile", label: "Account", icon: "👤" },
      ]
    : [
        { to: "/wisdom", label: "Wisdom", icon: "📖" },
        { to: "/events", label: "Events", icon: "🗓️" },
        { to: "/donate", label: "Support", icon: "💛" },
        { to: "/shop", label: "Shop", icon: "🛍️" },
        { to: "/login", label: "Account", icon: "✨" },
      ];

  const navItems = user ? authNavItems : publicNavItems;
  const requiredHeaderLinks = [
    { label: "Courses", href: "/courses" },
    { label: "Teachers", href: "/teachers" },
    { label: "Library", href: "/library" },
    { label: "Wellness", href: "/wellness" },
    { label: "Stories", href: "/stories" },
  ];

  const configuredHeaderLinks = settings.headerLinks
    .filter((item) => item.enabled && item.area === "header")
    .sort((left, right) => left.order - right.order);
  const configuredHrefSet = new Set(configuredHeaderLinks.map((item) => item.href));
  const augmentedHeaderLinks = [
    ...configuredHeaderLinks,
    ...requiredHeaderLinks
      .filter((item) => !configuredHrefSet.has(item.href))
      .map((item, index) => ({
        id: `required-${item.href.replace(/\//g, "") || "home"}`,
        label: item.label,
        href: item.href,
        kind: "internal" as const,
        area: "header" as const,
        enabled: true,
        order: configuredHeaderLinks.length + index + 1,
      })),
  ];
  const headerNavItems = configuredHeaderLinks.length > 0
    ? augmentedHeaderLinks.map((item) => ({ to: item.href, label: item.label, icon: "•", kind: item.kind }))
    : navItems.map((item) => ({ ...item, kind: "internal" as const }));
  const currentPath = location.pathname;
  const isActivePath = (to: string) => {
    if (to === "/") return currentPath === "/";
    return currentPath === to || currentPath.startsWith(`${to}/`);
  };
  const showMaintenance = settings.maintenanceMode && user?.role !== "admin";

  if (showMaintenance && currentPath !== "/login") {
    return (
      <div className="site-shell">
        <section className="maintenance-shell">
          <div className="container">
            <div className="maintenance-card">
              <p className="maintenance-card__eyebrow">Maintenance Mode</p>
              <h1>{settings.siteName} is temporarily offline</h1>
              <p>
                We are making updates to improve your experience. Please check back soon.
              </p>
              <Link to="/login" className="btn btn--brand">Admin Sign In</Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={`site-shell ${secondaryNavExpanded ? "secondary-rail-expanded" : "secondary-rail-collapsed"}`}>
      {/* Skip link for keyboard navigation */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* ── Sticky Header ─────────────────────────────────────── */}
      <header className="site-header">
        <div className="container site-header__inner">
          {/* Logo */}
          <Link to="/" className="site-logo" aria-label="WayToMoksha — home">
            <span className="site-logo__mark" aria-hidden="true">
              <img src="/brand/logo-mark.avif" alt="" loading="eager" decoding="async" />
            </span>
            <span className="site-logo__text">
              <span className="site-logo__name">{settings.siteName}</span>
              <span className="site-logo__tagline">{settings.tagline}</span>
            </span>
          </Link>

          <button
            type="button"
            className="nav-toggle"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
          >
            {mobileMenuOpen ? "✕" : "☰"}
          </button>

          {/* Primary nav */}
          <nav className="site-nav" aria-label="Primary navigation">
            {headerNavItems.map((item) => (
              item.kind === "external" ? (
                <a
                  key={item.to}
                  href={item.to}
                  className="nav-link"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="nav-link__icon" aria-hidden="true">{item.icon}</span>
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`nav-link${isActivePath(item.to) ? " active" : ""}`}
                >
                  <span className="nav-link__icon" aria-hidden="true">{item.icon}</span>
                  {item.label}
                </Link>
              )
            ))}
          </nav>

          {/* Auth actions */}
          <div className="site-header__actions">
            <button
              type="button"
              className="header-search-btn"
              aria-label="Search"
              onClick={() => setSearchOpen(true)}
            >
              🔍
            </button>
            {user ? (
              <>
                <Link
                  to="/cart"
                  className={`nav-link header-cart-link${isActivePath("/cart") ? " active" : ""}`}
                >
                  <span className="nav-link__icon" aria-hidden="true">🛒</span>
                  Cart
                  <span className="header-cart-count" aria-label={`${cartItemCount} items in cart`}>
                    {cartItemCount}
                  </span>
                </Link>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    className={`nav-link nav-link--admin${isActivePath("/admin") ? " active" : ""}`}
                    title="Site Admin"
                  >
                    ⚙️ Admin
                  </Link>
                )}
                <Link
                  to="/profile"
                  className={`header-profile-chip${isActivePath("/profile") ? " active" : ""}`}
                  aria-label="Open profile"
                  title="Profile"
                >
                  {profile?.avatar_url ? (
                    <img src={profile.avatar_url} alt="" className="header-profile-chip__img" />
                  ) : (
                    <span className="header-profile-chip__fallback">{profileInitial}</span>
                  )}
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="btn btn--outline-light btn--sm"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="btn btn--outline-light btn--sm">Sign in</Link>
                <Link to="/login" className="btn btn--gold btn--sm">Get started</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <div className={`mobile-nav-overlay${mobileMenuOpen ? " is-open" : ""}`} onClick={() => setMobileMenuOpen(false)} />
      <aside className={`mobile-nav-drawer${mobileMenuOpen ? " is-open" : ""}`} aria-label="Mobile navigation">
        <div className="mobile-nav-drawer__head">
          <span>Navigate</span>
          <button
            type="button"
            className="mobile-nav-drawer__close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            ✕
          </button>
        </div>

        <nav className="mobile-nav-section" aria-label="Main">
          <p className="mobile-nav-section__title">Main</p>
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`mobile-nav-link${isActivePath(item.to) ? " active" : ""}`}
            >
              <span aria-hidden="true">{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </nav>

        <nav className="mobile-nav-section" aria-label="Explore and quick actions">
          <p className="mobile-nav-section__title">Explore</p>
          {secondaryNavItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`mobile-nav-link${isActivePath(item.to) ? " active" : ""}`}
            >
              <span aria-hidden="true">{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </nav>

        {user ? (
          <div className="mobile-nav-actions">
            <Link to="/cart" className="mobile-nav-link">🛒 Cart ({cartItemCount})</Link>
            <button type="button" onClick={handleLogout} className="btn btn--outline">Sign out</button>
          </div>
        ) : (
          <div className="mobile-nav-actions">
            <Link to="/login" className="btn btn--outline">Sign in</Link>
            <Link to="/login" className="btn btn--gold">Get started</Link>
          </div>
        )}
      </aside>

      <aside className="secondary-rail" aria-label="Secondary navigation">
        <button
          type="button"
          className="secondary-rail__toggle"
          onClick={() => setSecondaryNavExpanded((prev) => !prev)}
          aria-expanded={secondaryNavExpanded}
          aria-label={secondaryNavExpanded ? "Collapse secondary navigation" : "Expand secondary navigation"}
          title={secondaryNavExpanded ? "Collapse" : "Expand"}
        >
          {secondaryNavExpanded ? "⟨" : "⟩"}
        </button>

        <nav className="secondary-rail__nav">
          {secondaryNavItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`secondary-rail__item${isActivePath(item.to) ? " active" : ""}`}
              title={item.label}
              aria-label={item.label}
            >
              <span className="secondary-rail__icon" aria-hidden="true">{item.icon}</span>
              <span className="secondary-rail__label">{item.label}</span>
            </Link>
          ))}
        </nav>
      </aside>

      <nav className="mobile-dock" aria-label="Mobile quick navigation">
        {mobileDockItems.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={`mobile-dock__item${isActivePath(item.to) ? " active" : ""}`}
          >
            <span aria-hidden="true">{item.icon}</span>
            <span>{item.label}</span>
          </Link>
        ))}
      </nav>

      {/* ── Page content ──────────────────────────────────────── */}
      <main className="site-main" id="main-content">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/content" element={<ContentPage />} />
          <Route path="/content/:slug" element={<ContentPage />} />
          <Route path="/wisdom" element={<ContentPage />} />
          <Route path="/wisdom/:slug" element={<ContentPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/shop" element={<ProductsPage />} />
          <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
          <Route path="/checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
          <Route path="/practice" element={<ProtectedRoute><PracticePage /></ProtectedRoute>} />
          <Route path="/mood" element={<ProtectedRoute><MoodPage /></ProtectedRoute>} />
          <Route path="/pass" element={<ProtectedRoute><PassPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/gamification" element={<ProtectedRoute><GamificationPage /></ProtectedRoute>} />
          <Route path="/events" element={<EventsListPage />} />
          <Route path="/events/:slug" element={<EventDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/donate" element={<DonatePage />} />
          <Route path="/volunteer" element={<VolunteerPage />} />
          <Route path="/support" element={<LegacyInfoPage variant="support" />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/healing" element={<HealingPage />} />
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/dreams" element={<ProtectedRoute><DreamsPage /></ProtectedRoute>} />
          <Route path="/badges" element={<ProtectedRoute><BadgesPage /></ProtectedRoute>} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/magazine" element={<MagazinePage />} />
          <Route path="/podcast" element={<PodcastPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/ops" element={<OpsShellPage />} />
          <Route path="/ops/checkin" element={<SenaRoute><CheckInPage /></SenaRoute>} />
          <Route path="/ops/pos" element={<SenaRoute><PosPage /></SenaRoute>} />
          <Route path="/ops/info" element={<SenaRoute><DonationInfoPage /></SenaRoute>} />
          <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
          {/* ── New platform routes ── */}
          <Route path="/courses" element={<CoursesPage />} />
          <Route path="/courses/:slug" element={<CourseDetailPage />} />
          <Route path="/teachers" element={<TeachersPage />} />
          <Route path="/teachers/:slug" element={<TeacherProfilePage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/wellness" element={<WellnessPage />} />
          <Route path="/stories" element={<StoriesPage />} />
          <Route path="/stories/:slug" element={<StoryDetailPage />} />
        </Routes>
      </main>

      {/* ── Global Search Overlay ─────────────────────────────── */}
      {searchOpen && (
        <div className="search-overlay" role="dialog" aria-modal="true" aria-label="Search">
          <div className="search-overlay__backdrop" onClick={() => setSearchOpen(false)} />
          <div className="search-overlay__box">
            <form onSubmit={handleSearchSubmit} className="search-overlay__form">
              <span className="search-overlay__icon" aria-hidden="true">🔍</span>
              <input
                autoFocus
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-overlay__input"
                placeholder="Search wisdom, events, blog, products…"
                aria-label="Search the site"
              />
              <button type="submit" className="btn btn--primary search-overlay__submit">Search</button>
              <button type="button" className="search-overlay__close" onClick={() => setSearchOpen(false)} aria-label="Close search">✕</button>
            </form>
            <div className="search-overlay__suggestions">
              <p className="search-overlay__hint">Quick links</p>
              {[
                { icon: "📚", label: "Wisdom Library", to: "/wisdom" },
                { icon: "🧘", label: "Daily Practice", to: "/practice" },
                { icon: "🗓️", label: "Upcoming Events", to: "/events" },
                { icon: "✍️", label: "Blog", to: "/blog" },
                { icon: "🎙️", label: "Podcast", to: "/podcast" },
                { icon: "📰", label: "Magazine", to: "/magazine" },
                { icon: "🛍️", label: "Sacred Shop", to: "/shop" },
                { icon: "🔮", label: "Healing Sessions", to: "/healing" },
              ].map((item) => (
                <button
                  key={item.to}
                  type="button"
                  className="search-overlay__quick-link"
                  onClick={() => { setSearchOpen(false); navigate(item.to); }}
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Footer ────────────────────────────────────────────── */}
      <SiteFooter
        siteName={settings.siteName}
        tagline={settings.tagline}
        showNewsletter={settings.enableNewsletter}
        footerLinks={settings.footerLinks}
      />
    </div>
  );
}

/* ── Site Footer ──────────────────────────────────────────────────── */
function SiteFooter({
  siteName,
  tagline,
  showNewsletter,
  footerLinks,
}: {
  siteName: string;
  tagline: string;
  showNewsletter: boolean;
  footerLinks: Array<{ id: string; label: string; href: string; kind: "internal" | "external"; area: "header" | "footer"; enabled: boolean; order: number }>;
}) {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  return (
    <footer className="site-footer">
      <div className="site-footer__top">
        <div className="container">
          <div className="site-footer__grid">
            {/* Brand column */}
            <div>
              <div className="footer-brand__name">{siteName}</div>
              <p className="footer-brand__desc">{tagline}</p>
              {showNewsletter && (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!email.trim()) return;
                    setSubscribed(true);
                    setEmail("");
                  }}
                >
                  {subscribed ? (
                    <p style={{ fontSize: "0.9rem", color: "#fef3c7" }}>
                      ✓ You're subscribed — thank you for joining.
                    </p>
                  ) : (
                    <div className="newsletter-form" style={{ marginTop: 0 }}>
                      <input
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                        placeholder="Your email"
                        autoComplete="email"
                        required
                      />
                      <button className="btn btn--gold" type="submit">Subscribe</button>
                    </div>
                  )}
                </form>
              )}
              <div className="social-links" style={{ marginTop: "1.5rem" }}>
                <a href="#" className="social-link" aria-label="Instagram">IG</a>
                <a href="#" className="social-link" aria-label="Facebook">FB</a>
                <a href="#" className="social-link" aria-label="YouTube">YT</a>
                <a href="#" className="social-link" aria-label="Twitter / X">𝕏</a>
              </div>
            </div>

            {/* Explore */}
            <div>
              <div className="footer-heading">Explore</div>
              <div className="footer-links">
                <Link to="/wisdom"    className="footer-link">Daily Wisdom</Link>
                <Link to="/courses"   className="footer-link">Courses</Link>
                <Link to="/teachers"  className="footer-link">Teachers</Link>
                <Link to="/library"   className="footer-link">Wisdom Library</Link>
                <Link to="/wellness"  className="footer-link">Wellness Hub</Link>
                <Link to="/practice"  className="footer-link">Practice Tracker</Link>
                <Link to="/shop"      className="footer-link">Sacred Shop</Link>
              </div>
            </div>

            {/* Stories */}
            <div>
              <div className="footer-heading">Stories &amp; Media</div>
              <div className="footer-links">
                <Link to="/stories"    className="footer-link">Community Stories</Link>
                <Link to="/blog"       className="footer-link">Blog</Link>
                <Link to="/magazine"   className="footer-link">Moksha Magazine</Link>
                <Link to="/podcast"    className="footer-link">Podcast</Link>
                <Link to="/portfolio"  className="footer-link">Portfolio</Link>
                <Link to="/badges"     className="footer-link">Badge Vault</Link>
                <Link to="/leaderboard" className="footer-link">Leaderboard</Link>
              </div>
            </div>

            {/* Community */}
            <div>
              <div className="footer-heading">Community</div>
              <div className="footer-links">
                <Link to="/events"    className="footer-link">Events &amp; Workshops</Link>
                <Link to="/about"     className="footer-link">About Us</Link>
                <Link to="/volunteer" className="footer-link">Volunteer</Link>
                <Link to="/donate"    className="footer-link">Donate</Link>
                <Link to="/contact"   className="footer-link">Contact</Link>
              </div>
            </div>

            {/* Support */}
            <div>
              <div className="footer-heading">Support</div>
              <div className="footer-links">
                <Link to="/support" className="footer-link">Help Center</Link>
                <Link to="/support" className="footer-link">Privacy Policy</Link>
                <Link to="/support" className="footer-link">Terms of Service</Link>
                <Link to="/register" className="footer-link">Create Account</Link>
                <Link to="/forgot-password" className="footer-link">Reset Password</Link>
              </div>
            </div>

            <div>
              <div className="footer-heading">Quick Links</div>
              <div className="footer-links">
                {footerLinks
                  .filter((item) => item.enabled && item.area === "footer")
                  .sort((left, right) => left.order - right.order)
                  .map((item) =>
                    item.kind === "external" ? (
                      <a key={item.id} href={item.href} className="footer-link" target="_blank" rel="noreferrer">
                        {item.label}
                      </a>
                    ) : (
                      <Link key={item.id} to={item.href} className="footer-link">
                        {item.label}
                      </Link>
                    )
                  )}
              </div>
            </div>
          </div>

          <div className="site-footer__bottom">
            <span className="footer-copyright">
              © {new Date().getFullYear()} {siteName}. All rights reserved. Made for seekers worldwide.
            </span>
            <nav className="footer-legal" aria-label="Legal">
              <Link to="/support">Privacy</Link>
              <Link to="/support">Terms</Link>
              <Link to="/contact">Contact</Link>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
}
